import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Zap, Mail, Lock, User, Crown, ShieldQuestion } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your mother's maiden name?",
  "What was the name of your first school?",
  "What is your favorite sports team?",
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || "");

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    name: "", email: "", password: "",
    security_question: SECURITY_QUESTIONS[0],
    security_answer: "",
  });

  // Helper function to filter out platform-injected errors
  const safeSetError = (error) => {
    const msg = error?.message || error || "";
    // Filter out DOMException clone errors from platform scripts
    if (msg.includes("clone") || msg.includes("cloned") || 
        msg.includes("DataCloneError") || msg.includes("body stream")) {
      // Don't show these errors - they're false positives from platform scripts
      return;
    }
    setError(msg);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Use XMLHttpRequest to avoid "object cannot be cloned" DOMException
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/auth/login`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.detail || "Invalid credentials"));
            }
          } catch (parseError) {
            reject(new Error("Login failed"));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Network error. Please try again."));
        };
        
        xhr.send(JSON.stringify(loginData));
      });

      login(result.user);
      navigate("/");
    } catch (err) {
      safeSetError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Use XMLHttpRequest to avoid "object cannot be cloned" DOMException
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/auth/register`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.detail || "Registration failed"));
            }
          } catch (parseError) {
            reject(new Error("Registration failed"));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Network error. Please try again."));
        };
        
        xhr.send(JSON.stringify(registerData));
      });

      login(result.user);
      navigate("/");
    } catch (err) {
      safeSetError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Crown className="w-10 h-10 text-emerald-600" />
          <span className="text-3xl font-bold text-white font-heading italic">WarMap</span>
        </div>

        <Card className="bg-[#1A1A1A] border-white/10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white font-heading">Welcome Back</CardTitle>
            <CardDescription className="text-gray-400">
              Sign in to your revenue command center
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-white/5">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-gray-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        id="login-email"
                        data-testid="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-gray-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        id="login-password"
                        data-testid="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="login-submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-800 to-emerald-600 hover:from-emerald-700 hover:to-emerald-500"
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>

                  <div className="text-right">
                    <Link to="/forgot-password" className="text-xs text-emerald-400 hover:text-emerald-300" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-gray-300">Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        id="register-name"
                        data-testid="register-name"
                        type="text"
                        placeholder="Your name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-gray-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        id="register-email"
                        data-testid="register-email"
                        type="email"
                        placeholder="you@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-gray-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        id="register-password"
                        data-testid="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300 flex items-center gap-1">
                      <ShieldQuestion className="w-3 h-3" /> Security Question
                    </Label>
                    <Select
                      value={registerData.security_question}
                      onValueChange={(v) => setRegisterData({ ...registerData, security_question: v })}
                    >
                      <SelectTrigger data-testid="register-security-question" className="bg-white/5 border-white/10 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {SECURITY_QUESTIONS.map((q) => (
                          <SelectItem key={q} value={q} className="text-white text-sm">{q}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Security Answer</Label>
                    <Input
                      data-testid="register-security-answer"
                      type="text"
                      placeholder="Your answer"
                      value={registerData.security_answer}
                      onChange={(e) => setRegisterData({ ...registerData, security_answer: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="register-submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-800 to-emerald-600 hover:from-emerald-700 hover:to-emerald-500"
                  >
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#1A1A1A] px-2 text-gray-500">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              data-testid="google-login"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-xs text-gray-500 mt-4">
              Demo: demo@repvision.app / demo123
            </p>

            <div className="mt-4 pt-4 border-t border-white/5 flex justify-center">
              <Link
                to="/superadmin"
                data-testid="superadmin-login-link"
                className="text-xs text-gray-500 hover:text-amber-400 inline-flex items-center gap-1.5 transition-colors"
              >
                <Crown className="w-3 h-3" />
                Super Admin login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
